namespace $.$$ {

	type $shm_projects_test_entry = {
		id: string
		manifest: string
		status: string
		started_at: string
		duration_ms: number
		scenario: {
			id: string
			title: string
			source: string
		}
	}

	type $shm_projects_test_index = {
		runs: readonly $shm_projects_test_entry[]
	}

	export class $shm_projects_tests extends $.$shm_projects_tests {

		project() {
			return this.$.$shm_projects_data.record( this.project_id() )
		}

		index_uri() {
			return this.$.$shm_projects_config.resource_url( 'shm/tests/results/index.json' )
		}

		@ $mol_mem
		index() {
			return this.$.$mol_fetch.json( this.index_uri(), { cache: 'no-store' } ) as $shm_projects_test_index
		}

		scenario_ids() {
			return new Set( this.project()?.tests?.scenarios ?? [] )
		}

		entries() {
			const scenarios = this.scenario_ids()
			if( !scenarios.size ) return [] as readonly $shm_projects_test_entry[]
			const latest = new Map< string, $shm_projects_test_entry >()
			for( const entry of this.index().runs ) {
				if( !scenarios.has( entry.scenario.id ) || latest.has( entry.scenario.id ) ) continue
				latest.set( entry.scenario.id, entry )
			}
			return [ ...latest.values() ]
		}

		entries_filtered() {
			const query = this.filter().trim().toLowerCase()
			if( !query ) return this.entries()
			return this.entries().filter( entry => {
				return `${ entry.scenario.title } ${ entry.scenario.source }`.toLowerCase().includes( query )
			} )
		}

		test_rows() {
			const entries = this.entries_filtered()
			return entries.length ? entries.map( entry => this.Test( entry.scenario.id ) ) : [ this.Empty() ]
		}

		entry( scenario: string ) {
			return this.entries().find( entry => entry.scenario.id === scenario ) ?? null
		}

		passed_count() {
			return this.entries().filter( entry => entry.status === 'passed' ).length
		}

		summary() {
			const total = this.entries().length
			return `${ this.project()?.title || 'Проект' }: ${ total } ${ this.plural( total, 'сценарий', 'сценария', 'сценариев' ) }, успешно ${ this.passed_count() }`
		}

		plural( count: number, one: string, few: string, many: string ) {
			const mod100 = count % 100
			if( mod100 >= 11 && mod100 <= 14 ) return many
			const mod10 = count % 10
			return mod10 === 1 ? one : mod10 >= 2 && mod10 <= 4 ? few : many
		}

		test_status( scenario: string ) {
			return this.entry( scenario )?.status === 'passed' ? '✓ Успешно' : '✕ Ошибка'
		}

		test_title( scenario: string ) {
			return ({
				'map-large-journey-video': 'Карта: большой пользовательский сценарий',
				'map-pb-admin-widget-smoke': 'PocketBase-админка как виджет карты',
				'project-static-catalog-smoke': 'Фиксированный каталог проектов',
				'dashboard-dashboard-navigation-smoke': 'Навигация разделов Dashboard',
				'ba-ba-wordstat-task-file-video': 'Wordstat: задача из файла',
				'ba-ba-mentionstat-mock-video': 'Статистика упоминаний: полный сценарий',
				'projects-projects-goal-journey': 'Разработка и выпуск проекта',
			} as Record< string, string >)[ scenario ] ?? this.entry( scenario )?.scenario.title ?? scenario
		}

		test_source( scenario: string ) {
			return this.entry( scenario )?.scenario.source.replace( /^\.\.\//, '' ) || ''
		}

		test_started( scenario: string ) {
			const date = this.entry( scenario )?.started_at
			return date ? new Date( date ).toLocaleString( 'ru-RU' ) : '—'
		}

		test_duration( scenario: string ) {
			const milliseconds = this.entry( scenario )?.duration_ms ?? 0
			return milliseconds < 1000 ? `${ milliseconds } мс` : `${ ( milliseconds / 1000 ).toFixed( 1 ) } с`
		}

		test_uri( scenario: string ) {
			const entry = this.entry( scenario )
			if( !entry ) return ''
			const app = this.$.$shm_projects_config.preview_url( 'shm/tests/app' )
			return `${ app }#!test_scenario=${ encodeURIComponent( scenario ) }/test_run=${ encodeURIComponent( entry.id ) }`
		}

		version_tests(): readonly $shm_projects_data_test[] {
			return this.entries().map( entry => ({
				scenario: entry.scenario.id,
				title: this.test_title( entry.scenario.id ),
				run: entry.id,
				status: entry.status,
				manifest: entry.manifest,
			}) )
		}

	}

}
